import { ArrowUpRight, Sparkles, Car, Droplets, Phone, Wrench, Info } from "lucide-react";
import { PHONE_TEL, PHONE_DISPLAY } from "../../lib/business";

const HatchbackIcon = (props) => (
  <svg
    viewBox="0 0 32 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {/* Sloped 2-door hatchback silhouette */}
    <path d="M2 14 L2 11 L5 9 L11 4 L21 4 L26 9 L30 11 L30 14" />
    <path d="M11 4 L13 9 L26 9" />
    <path d="M2 14 L7 14 M11 14 L19 14 M23 14 L30 14" />
    <circle cx="9" cy="14.5" r="2.2" fill="none" />
    <circle cx="21" cy="14.5" r="2.2" fill="none" />
  </svg>
);

const SedanIcon = (props) => (
  <svg
    viewBox="0 0 48 22"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.3"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {/* Toyota Camry-style sedan silhouette: long hood, sloped A-pillar, split greenhouse, defined trunk */}
    <path d="M2 16 L2 13 L4 12 L7 11 L12 5.5 C14 4.5 16 4 19 4 L29 4 C31.5 4 33.5 4.6 35 5.8 L38 11 L43 12 L46 13.5 L46 16" />
    {/* Greenhouse with two windows + B-pillar */}
    <path d="M11.5 11 L13 6 L23 6 L23 11 M23 6 L33 6 L34.5 11" />
    {/* Bottom rocker line broken by wheels */}
    <path d="M2 16 L9 16 M17 16 L29 16 M37 16 L46 16" />
    {/* Front grille hint */}
    <path d="M2 13 L4 13" />
    {/* Wheels */}
    <circle cx="13" cy="16.5" r="2.6" />
    <circle cx="13" cy="16.5" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="33" cy="16.5" r="2.6" />
    <circle cx="33" cy="16.5" r="0.9" fill="currentColor" stroke="none" />
  </svg>
);

const SuvIcon = (props) => (
  <svg
    viewBox="0 0 36 22"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {/* Taller boxier SUV with long roofline */}
    <path d="M2 15 L2 11 L4 10 L6 3 L28 3 L31 10 L34 11 L34 15" />
    <path d="M6 10 L6 3 M20 3 L20 10 M28 3 L28 10 M6 10 L31 10" />
    <path d="M2 15 L7 15 M11 15 L22 15 M26 15 L34 15" />
    <circle cx="9" cy="15.5" r="2.4" fill="none" />
    <circle cx="24" cy="15.5" r="2.4" fill="none" />
  </svg>
);

const SERVICES = [
  {
    id: "full",
    title: "Full Wash & Detailing",
    priceTiers: [
      { icon: "hatch", label: "Small hatchback", sub: "2-door", price: "$249" },
      { icon: "sedan", label: "Sedan", sub: "Medium size", price: "$309" },
      { icon: "suv", label: "SUV / 4WD / Ute", sub: "Large size", price: "$359" },
    ],
    blurb:
      "The complete treatment — inside out. Interior + exterior, finished with a hand polish for a true showroom shine.",
    bullets: [
      "Everything in Interior",
      "Everything in Exterior",
      "Hand polish & sealant",
      "Engine bay wash & service",
      "Final inspection walk-around",
    ],
    image:
      "https://images.pexels.com/photos/6026083/pexels-photo-6026083.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    icon: Car,
    span: "md:col-span-7",
  },
  {
    id: "interior",
    title: "Interior Detailing",
    priceTiers: [
      { icon: "hatch", label: "Small hatchback", sub: "2-door", price: "$169" },
      { icon: "sedan", label: "Sedan", sub: "Medium size", price: "$199" },
      { icon: "suv", label: "SUV / 4WD / Ute", sub: "Large size", price: "$259" },
    ],
    blurb:
      "Deep vacuum, seat & upholstery shampoo, carpet & floor shampoo, dashboard wipe-down, leather treatment and odour neutralisation.",
    bullets: [
      "Seat & upholstery shampoo",
      "Carpet & floor shampoo",
      "Leather & vinyl conditioning",
      "Streak-free interior glass",
      "Dash & console detail",
      "Door panel clean",
      "Stain removal",
      "Air vents & cup-holders",
    ],
    andMore: true,
    image:
      "https://images.unsplash.com/photo-1708805282695-ef186db20192?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    icon: Sparkles,
    span: "md:col-span-5",
  },
  {
    id: "exterior",
    title: "Exterior Wash & Detailing",
    priceTiers: [
      { icon: "hatch", label: "Small hatchback", sub: "2-door", price: "$169" },
      { icon: "sedan", label: "Sedan", sub: "Medium size", price: "$209" },
      { icon: "suv", label: "SUV / 4WD / Ute", sub: "Large size", price: "$279" },
    ],
    blurb:
      "Two-bucket hand wash, wheel & tyre treatment, snow-foam, window cleaning, hand polish and wax for a deep, even shine.",
    bullets: [
      "Two-bucket safe wash",
      "Wheels, arches & tyres",
      "Bug, tar & sap removal",
      "Window cleaning",
      "Hand polish",
      "Wax",
    ],
    andMore: true,
    image:
      "https://images.pexels.com/photos/15469368/pexels-photo-15469368.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    icon: Droplets,
    span: "md:col-span-7",
  },
  {
    id: "custom",
    title: "Custom Service",
    blurb:
      "Need something specific? From engine bay cleans to pet hair removal, headlight restoration or one-off jobs — call us and we'll tailor a package to your car.",
    bullets: [],
    image:
      "https://images.pexels.com/photos/3354648/pexels-photo-3354648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    icon: Wrench,
    span: "md:col-span-5",
  },
];

function ServiceCard({ s }) {
  const Icon = s.icon;
  return (
    <article
      data-testid={`service-card-${s.id}`}
      className={`${s.span} group relative overflow-hidden rounded-3xl border border-white/10 bg-zinc-900 transition-transform duration-300 hover:-translate-y-1`}
    >
      <div className={`relative ${s.wide ? "min-h-[420px]" : "min-h-[480px]"}`}>
        <img
          src={s.image}
          alt={s.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />

        <div className={`relative h-full p-8 sm:p-12 flex flex-col ${s.wide ? "md:flex-row md:items-end md:justify-between" : ""}`}>
          <div className={`${s.wide ? "max-w-xl" : ""}`}>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/15 rounded-full px-3 py-1.5 mb-6">
              <Icon className="w-3.5 h-3.5 text-[#E10600]" />
              <span className="font-display text-[11px] uppercase tracking-widest text-white/90">
                Service
              </span>
            </div>
            <h3
              data-testid={`service-title-${s.id}`}
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white"
            >
              {s.title}
            </h3>
            <p className="mt-3 font-body text-zinc-300 max-w-md leading-relaxed">
              {s.blurb}
            </p>
            {s.price && (
              <div
                data-testid={`service-price-${s.id}`}
                className="mt-5 inline-flex items-baseline gap-2"
              >
                <span className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight">
                  {s.price}
                </span>
                {s.priceNote && (
                  <span className="font-body text-xs sm:text-sm text-zinc-400">
                    — {s.priceNote}
                  </span>
                )}
              </div>
            )}
            {s.priceTiers && (
              <div
                data-testid={`service-tiers-${s.id}`}
                className="mt-6 grid grid-cols-3 gap-2 sm:gap-3 max-w-md"
              >
                {s.priceTiers.map((t, i) => {
                  const TierIcon =
                    t.icon === "hatch"
                      ? HatchbackIcon
                      : t.icon === "sedan"
                      ? SedanIcon
                      : SuvIcon;
                  return (
                    <div
                      key={t.label}
                      data-testid={`tier-${s.id}-${i}`}
                      className="relative rounded-2xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] hover:border-[#E10600]/40 transition-colors p-3 sm:p-4 flex flex-col items-center text-center"
                    >
                      {t.image ? (
                        <img
                          src={t.image}
                          alt={t.label}
                          loading="lazy"
                          className="w-20 h-10 object-cover object-center rounded-md mb-2"
                        />
                      ) : (
                        <TierIcon
                          className={`text-[#E10600] mb-2 ${
                            t.icon === "hatch"
                              ? "w-10 h-7"
                              : t.icon === "sedan"
                              ? "w-14 h-7"
                              : "w-12 h-8"
                          }`}
                        />
                      )}
                      <span className="font-display text-[10px] sm:text-[11px] uppercase tracking-wider text-zinc-400">
                        {t.sub}
                      </span>
                      <span className="font-body text-xs sm:text-sm text-white/90 leading-tight mt-0.5">
                        {t.label}
                      </span>
                      <span className="mt-2 font-display text-xl sm:text-2xl font-black text-white tracking-tight">
                        {t.price}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {s.bullets.length > 0 && (
            <ul
              className={`mt-6 grid grid-cols-2 gap-x-6 gap-y-2 ${
                s.wide ? "md:mt-0 md:max-w-md" : ""
              }`}
            >
              {s.bullets.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-2 font-body text-sm text-zinc-200"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#E10600] shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
          {s.andMore && (
            <div className="mt-3 font-body text-sm italic text-zinc-400">
              …and more
            </div>
          )}

          <a
            href={PHONE_TEL}
            data-testid={`service-call-${s.id}`}
            className="absolute top-6 right-6 inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#E10600] hover:bg-[#FF2D1A] text-white transition-colors"
            aria-label={`Call to book ${s.title}`}
          >
            <ArrowUpRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#09090B] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="font-display text-xs uppercase tracking-[0.3em] text-[#E10600] mb-3">
              / Services
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white max-w-2xl">
              Built for Cars That
              <br />
              Deserve Better.
            </h2>
          </div>
          <p className="font-body text-zinc-400 max-w-md">
            Three honest packages, zero gimmicks. Whatever your ride needs, we
            roll up to your driveway in Newcastle and leave it gleaming.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {SERVICES.map((s) => (
            <ServiceCard key={s.id} s={s} />
          ))}
        </div>

        <div
          data-testid="services-buffing-note"
          className="mt-8 flex items-start gap-3 p-5 sm:p-6 rounded-2xl border border-[#E10600]/30 bg-[#E10600]/5"
        >
          <Info className="w-5 h-5 text-[#E10600] shrink-0 mt-0.5" />
          <div className="font-body text-sm sm:text-[15px] text-zinc-200 leading-relaxed">
            <span className="font-display uppercase tracking-wide font-bold text-white mr-1">
              Please note:
            </span>
            As a fully mobile service, we no longer offer machine buffing — it&apos;s a time-intensive process that isn&apos;t practical on the road. All polishing is done by hand for a clean, even finish.
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 sm:p-8 rounded-2xl border border-white/10 bg-zinc-900/60">
          <div>
            <div className="font-display text-xl sm:text-2xl uppercase font-bold text-white">
              Not sure which one?
            </div>
            <div className="font-body text-sm text-zinc-400">
              Give us a quick call — we&apos;ll recommend the right package for
              your vehicle.
            </div>
          </div>
          <a
            href={PHONE_TEL}
            data-testid="services-bottom-call"
            className="inline-flex items-center gap-2 bg-[#E10600] hover:bg-[#FF2D1A] text-white font-display uppercase tracking-wide font-bold px-6 py-4 rounded-full transition-colors"
          >
            <Phone className="w-4 h-4" />
            Call {PHONE_DISPLAY}
          </a>
        </div>
      </div>
    </section>
  );
}
